<template>
    <div v-show="!show" class="kline-loading"><a-spin /></div>
    <div v-show="show" class="kline-container" ref="kline"></div>
</template>
<script>
import { createChart } from "lightweight-charts"
import currencyApi from '@/common/api/currency';
import { klineRequestQueue } from '@/common/util/requestQueue';
import { normalizeKlineTheme } from '@/common/util/klineTheme';

export default {
    name:"kline",
    props: {
        kData: {
            default: []
        },
        change: {
            default: 0
        },
        currencyId:{
            type:Number,
            default:0
        }
    },
    components: {
    },
    data() {
        return {
            chart: null,
            lineSeries: null,
            show:false
        }
    },
    watch: {
        currencyId(v){
            console.log(v);
        },
        '$store.state.userTheme'(newTheme, oldTheme) {
            if (newTheme !== oldTheme) {
                this.applyTheme(newTheme);
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.init()
        })
    },
    beforeUnmount() {
        // 组件销毁前清理图表实例
        if (this.chart) {
            try {
                this.chart.remove();
            } catch (e) {
                console.warn('清理图表实例失败:', e);
            }
            this.chart = null;
            this.lineSeries = null;
        }
    },
    methods: {
        getTheme() {
            return normalizeKlineTheme(this.$store.state.userTheme);
        },
        getThemeOptions(theme = this.getTheme()) {
            const isLight = normalizeKlineTheme(theme) === 'light';
            return {
                background: isLight ? '#fff' : '#1f1f1f',
                textColor: isLight ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'
            };
        },
        applyTheme(theme) {
            const options = this.getThemeOptions(theme);

            if (this.chart) {
                this.chart.applyOptions({
                    layout: {
                        background: { color: options.background },
                        textColor: options.textColor
                    }
                });
            }

            if (this.lineSeries) {
                this.lineSeries.applyOptions({
                    topColor: options.background,
                    bottomColor: options.background
                });
            }
        },
        syncToInterval() {
            if (this.lineSeries) {
                this.chart.removeSeries(this.lineSeries);
                this.lineSeries = null;
            }
            const themeOptions = this.getThemeOptions();
            this.lineSeries = this.chart.addAreaSeries({
                topColor: themeOptions.background,
                bottomColor: themeOptions.background,
                lineColor:  Number(this.change) >= 0 ? "#13b26f" : '#dd4b4b', // #13b26f 绿色 #dd4b4b 红
                lineWidth: 2,
                priceFormat: {
                    type: "price",
                },
                priceLineVisible: !1,
            });
            let kData = JSON.parse(JSON.stringify(this.kData))
            const formattedData = this.formatKlineData(kData);
            
            if (formattedData.length === 0) {
                console.warn('⚠️ 同步K线数据为空，跳过更新');
                return;
            }
            
            try {
                this.lineSeries.setData(formattedData);
                console.log('✅ K线图表同步成功，数据点数量:', formattedData.length);
            } catch (error) {
                console.error('❌ 同步K线图表数据失败:', error);
            }
        },
        init() {
            // 确保DOM元素存在
            if (!this.$refs.kline) {
                console.warn('⚠️ K线容器元素不存在，延迟初始化');
                setTimeout(() => {
                    if (this.$refs.kline) {
                        this.init();
                    }
                }, 100);
                return;
            }
            
            // 检查元素是否真的在DOM中
            if (!this.$refs.kline.parentNode) {
                console.warn('⚠️ K线容器元素未挂载到DOM，延迟初始化');
                setTimeout(() => {
                    if (this.$refs.kline && this.$refs.kline.parentNode) {
                        this.init();
                    }
                }, 100);
                return;
            }
            
            // 使用请求队列，避免同时发起大量请求
            console.log(`🎯 K线组件准备请求，币种ID: ${this.currencyId}`);
            console.log('📦 请求队列对象:', klineRequestQueue);
            
            klineRequestQueue.add(() => {
                console.log(`🔥 实际发起请求，币种ID: ${this.currencyId}`);
                return currencyApi.contractLine({
                    interval: '1H',
                    currency_id: this.currencyId,
                    limit:48
                });
            }).then(res=>{
                console.log(`✅ 请求完成，币种ID: ${this.currencyId}`);
                
                // 再次检查组件是否仍然挂载
                if (!this.$refs.kline || !this.$refs.kline.parentNode) {
                    console.warn('⚠️ K线容器元素在API响应时已不存在，取消初始化');
                    return;
                }
                
                if(res.code !== 200){
                    console.warn('K线数据API返回错误:', res);
                    return false;
                }
                
                if (!res.data || !Array.isArray(res.data)) {
                    console.warn('K线数据格式无效:', res.data);
                    return false;
                }
                
                // 如果已有图表实例，先清理
                if (this.chart) {
                    try {
                        this.chart.remove();
                    } catch (e) {
                        console.warn('清理旧图表实例失败:', e);
                    }
                    this.chart = null;
                    this.lineSeries = null;
                }
                
                // 最后一次检查元素是否存在
                if (!this.$refs.kline || !this.$refs.kline.parentNode) {
                    console.warn('⚠️ 创建图表前检查：K线容器元素不存在');
                    return;
                }
                
                const themeOptions = this.getThemeOptions();
                try {
                    this.chart = createChart(this.$refs.kline, {
                    width: 120,
                    height: 40,
                    watermark: {
                        visible: !1
                    },
                    grid: {
                        vertLines: {
                            visible: !1
                        },
                        horzLines: {
                            visible: !1
                        }
                    },
                    layout: {
                        background: {
                            color: themeOptions.background
                        },
                        textColor: themeOptions.textColor,
                        attributionLogo: false
                    },
                    crosshair: {
                        mode: 'Magnet',
                        vertLine: {
                            visible: !1,
                            labelVisible: !1,
                            labelBackgroundColor: "#fff"
                        },
                        horzLine: {
                            visible: !1,
                            labelVisible: !1,
                            labelBackgroundColor: "#fff"
                        }
                    },
                    timeScale: {
                        visible: !1
                    },
                    rightPriceScale: {
                        visible: !1
                    },
                    handleScale: {
                        mouseWheel: !1
                    },
                    handleScroll: {
                        mouseWheel: !1,
                        pressedMouseMove: !1,
                        horzTouchDrag: !1,
                        vertTouchDrag: !1
                    }
                    });
                } catch (error) {
                    console.error('❌ 创建K线图表失败:', error);
                    this.show = false;
                    return;
                }
                
                if (!this.chart) {
                    console.error('❌ 图表创建失败，chart为null');
                    this.show = false;
                    return;
                }
                
                try {
                    this.chart.timeScale().applyOptions({
                    barSpacing:7,
                    borderColor: '#71649C',
                    timeVisible:false,
                    secondsVisible:false,
                    scrollToRealTime:false,
                    tickMarkFormatter:(time,tickMarkType,locale)=>{
                        let date = new Date(time);  // 将微秒转换为毫秒
                        let hours = String(date.getHours()).padStart(2, '0');
                        let minutes = String(date.getMinutes()).padStart(2, '0');
                        let seconds = String(date.getSeconds()).padStart(2, '0');
                        return `${hours}:${minutes}:${seconds}`;
                    }
                    });
                } catch (error) {
                    console.error('❌ 设置时间轴选项失败:', error);
                }

                try {
                    this.lineSeries = this.chart.addAreaSeries({
                    topColor: themeOptions.background,
                    bottomColor: themeOptions.background,
                    lineColor:  Number(this.change) >= 0 ? "#13b26f" : '#dd4b4b', // #13b26f 绿色 #dd4b4b 红
                    lineWidth: 2,
                    priceFormat: {
                        type: "price",
                    },
                    priceLineVisible: !1,
                    });
                } catch (error) {
                    console.error('❌ 添加面积系列失败:', error);
                    this.show = false;
                    return;
                }
                
                // 转换数据格式以适配lightweight-charts
                const formattedData = this.formatKlineData(res.data);
                
                if (formattedData.length === 0) {
                    console.warn('⚠️ 格式化后的K线数据为空，无法显示图表');
                    this.show = false;
                    return;
                }
                
                try {
                    this.lineSeries.setData(formattedData);
                    this.show = true;
                    console.log('✅ K线图表数据设置成功，数据点数量:', formattedData.length);
                } catch (error) {
                    console.error('❌ 设置K线图表数据失败:', error);
                    this.show = false;
                }
            }).catch(error => {
                console.error('❌ 获取K线数据失败:', error);
                this.show = false;
            })
        },
        
        // 格式化K线数据以适配lightweight-charts
        formatKlineData(data) {
            console.log('🔍 格式化K线数据:', data);
            
            if (!Array.isArray(data)) {
                console.warn('K线数据格式错误:', data);
                return [];
            }
            
            const formattedData = data.map(item => {
                // 处理时间戳 - 支持多种格式
                let time = item.time || item.timestamp || item.date || Date.now();
                
                // 如果time是字符串，转换为时间戳
                if (typeof time === 'string') {
                    time = new Date(time).getTime();
                } else if (typeof time === 'number') {
                    // 如果时间戳是秒，转换为毫秒
                    if (time < 1000000000000) {
                        time = time * 1000;
                    }
                }
                
                // 处理价格 - 优先使用close价格，然后是其他价格字段
                let value = item.close || item.c || item.price || item.value || 0;
                value = parseFloat(value) || 0;
                
                // 确保数据有效
                if (!time || value <= 0) {
                    console.warn('跳过无效数据项:', item);
                    return null;
                }
                
                return {
                    time: time,
                    value: value
                };
            }).filter(item => {
                // 过滤掉null和无效的数据项
                return item !== null && item.time && item.value > 0;
            });
            
            console.log('✅ 格式化后的K线数据:', formattedData);
            return formattedData;
        }
    }
}
</script>

<style scoped>
.kline-container {
  width: 120px;
  height: 40px;
  min-height: 40px;
}

/* 确保加载状态居中 */
.arco-spin {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 40px;
}
</style>
